import { TransformControls } from '@react-three/drei';
import { createPortal, useThree } from '@react-three/fiber';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Group, Vector3 } from 'three';
import type { TransformControls as TransformControlsImpl } from 'three-stdlib';
import {
  keepTranslateArrowsOutward,
  observeTransformControlsInteraction,
  removeTransformControlsPlaneHandles,
  TransformControlsInteractionState
} from 'utils/transform-controls';

type TranslateGizmo3DProps = {
  position: [number, number, number];
  showY?: boolean;
  children?: ReactNode;
  onDragStart: () => void;
  onDragChange: (anchorPosition: Vector3) => void;
  onDragEnd: (anchorPosition: Vector3 | null) => void;
  onInteractionChange?: (state: TransformControlsInteractionState) => void;
};

export default function TranslateGizmo3D({
  position,
  showY = true,
  children,
  onDragStart,
  onDragChange,
  onDragEnd,
  onInteractionChange
}: TranslateGizmo3DProps) {
  const scene = useThree((state) => state.scene);
  const anchorRef = useRef<Group | null>(null);
  const controlsRef = useRef<TransformControlsImpl | null>(null);
  const controlsForDisposalRef = useRef<TransformControlsImpl | null>(null);
  const [anchorObject, setAnchorObject] = useState<Group | null>(null);
  const draggingRef = useRef(false);
  const positionRef = useRef(position);
  const onDragEndRef = useRef(onDragEnd);
  const onInteractionChangeRef = useRef(onInteractionChange);

  positionRef.current = position;
  onDragEndRef.current = onDragEnd;
  onInteractionChangeRef.current = onInteractionChange;

  const [targetX, targetY, targetZ] = position;

  const setAnchor = useCallback((group: Group | null) => {
    anchorRef.current = group;
    if (group) {
      const [x, y, z] = positionRef.current;
      group.position.set(x, y, z);
    }
    setAnchorObject(group);
  }, []);

  useLayoutEffect(() => {
    if (!draggingRef.current && anchorRef.current) {
      anchorRef.current.position.set(targetX, targetY, targetZ);
    }
  }, [targetX, targetY, targetZ]);

  useEffect(() => {
    return () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        onDragEndRef.current(null);
      }
      controlsForDisposalRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (!anchorObject) return undefined;
    const controls = controlsRef.current;
    if (!controls) return undefined;

    controlsForDisposalRef.current = controls;
    removeTransformControlsPlaneHandles(controls);
    const restoreArrowFlipBehavior = keepTranslateArrowsOutward(controls);

    const unobserveInteraction = observeTransformControlsInteraction(
      controls,
      ({ hovered, dragging }) => {
        onInteractionChangeRef.current?.({ hovered, dragging });
        if (dragging) {
          document.body.style.cursor = 'grabbing';
        } else if (hovered) {
          document.body.style.cursor = 'grab';
        } else {
          document.body.style.cursor = '';
        }
      }
    );

    return () => {
      unobserveInteraction();
      restoreArrowFlipBehavior();
      onInteractionChangeRef.current?.({ hovered: false, dragging: false });
      document.body.style.cursor = '';
    };
  }, [anchorObject]);

  const handleMouseDown = useCallback(() => {
    draggingRef.current = true;
    onDragStart();
  }, [onDragStart]);

  const handleObjectChange = useCallback(() => {
    if (!draggingRef.current || !anchorRef.current) return;
    onDragChange(anchorRef.current.position);
  }, [onDragChange]);

  const handleMouseUp = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onDragEnd(anchorRef.current ? anchorRef.current.position : null);
  }, [onDragEnd]);

  return (
    <>
      <group ref={setAnchor}>{children}</group>
      {anchorObject &&
        createPortal(
          <TransformControls
            ref={controlsRef}
            object={anchorObject}
            mode='translate'
            space='world'
            size={0.7}
            showX
            showZ
            showY={showY}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onObjectChange={handleObjectChange}
          />,
          scene
        )}
    </>
  );
}
